import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight, Sparkles, Tv, Bus, Monitor, Film, Clapperboard, Radio, Quote } from "lucide-react";
import SectionHeading from "@/components/shared/SectionHeading";
import StatBlock from "@/components/shared/StatBlock";
import ClientMarquee from "@/components/shared/ClientMarquee";

const HERO_IMG = "https://images.pexels.com/photos/27207885/pexels-photo-27207885.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1100&w=1600";

const SERVICES = [
  { id: "billboards",        title: "Billboards",        icon: Tv,         desc: "Premium static outdoor in trophy locations.", img: "https://images.unsplash.com/photo-1604928593934-3ba7b68219ac?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400" },
  { id: "digital_hoardings", title: "Digital Hoardings", icon: Monitor,    desc: "LED, DOOH, and programmatic full-motion screens.", img: "https://images.unsplash.com/photo-1708107243374-52eddcc3fe64?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400" },
  { id: "transit",           title: "Transit",           icon: Bus,        desc: "Buses, metros, and autos in motion.", img: "https://images.pexels.com/photos/16973293/pexels-photo-16973293.jpeg?auto=compress&cs=tinysrgb&w=1200" },
  { id: "cinema",            title: "Cinema",            icon: Film,       desc: "Big-screen pre-show advertising in multiplexes.", img: "https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" },
  { id: "film",              title: "Film",              icon: Clapperboard, desc: "In-film placement and brand integrations.", img: "https://images.unsplash.com/photo-1485846234645-a62644f84728?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" },
  { id: "radio",             title: "Radio",             icon: Radio,      desc: "Drive-time spots across leading FM channels.", img: "https://images.unsplash.com/photo-1485579149621-3123dd979885?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" },
];

const TESTIMONIALS = [
  { quote: "Horizn turned our EV launch into a city-wide event. Highway dominance, airport visibility, and measurable lift, all in 14 days.", author: "Marketing Manager" },
  { quote: "The most data-driven OOH partner we've worked with. They sweat every panel, every daypart, every metric.", author: "Brand Manager" },
];

export default function Home() {
  return (
    <div data-testid="home-page" className="text-zinc-100">
      {/* HERO */}
      <section className="relative min-h-[100svh] overflow-hidden grain">
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt="City billboards at night"
            className="h-full w-full object-cover opacity-50 animate-flicker"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-zinc-950/70 via-zinc-950/40 to-zinc-950" />
          <div className="absolute inset-0 bg-gradient-to-r from-zinc-950 via-transparent to-zinc-950/40" />
        </div>
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10 pt-32 md:pt-40 pb-24">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="font-mono-accent text-[11px] text-amber-400 mb-6 flex items-center gap-3"
            data-testid="hero-overline"
          >
            <Sparkles className="h-3.5 w-3.5" />
            India's most ambitious OOH network
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.05 }}
            className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-[80px] font-black leading-[1] tracking-tighter max-w-[18ch]"
            data-testid="hero-title"
          >
            Where Brand Meets{" "}
            <span className="gradient-text italic font-extrabold">
              The Real World.
            </span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-7 max-w-2xl text-lg md:text-xl text-zinc-300 leading-relaxed"
            data-testid="hero-subtitle"
          >
            We engineer Out-of-Home campaigns that take over cities. Billboards,
            digital hoardings, transit, cinema, film, and radio. Bold visibility,
            measurable impact.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/contact"
              data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-2 rounded-full bg-white text-zinc-950 px-7 py-4 text-sm font-bold hover:bg-amber-300 transition-all"
            >
              Book a Campaign
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <Link
              to="/services"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-7 py-4 text-sm font-semibold text-white hover:bg-white/10 backdrop-blur transition-all"
            >
              Explore Media <ArrowRight className="h-4 w-4" />
            </Link>
          </motion.div>

          <div className="hidden md:flex absolute bottom-10 left-1/2 -translate-x-1/2 flex-col items-center gap-2 text-zinc-500">
            <span className="font-mono-accent text-[10px]">scroll</span>
            <div className="h-8 w-5 rounded-full border border-zinc-600 flex justify-center pt-1.5">
              <span className="h-1.5 w-1 rounded-full bg-zinc-400 animate-scroll-dot" />
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="relative bg-zinc-950 border-t border-white/5">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-20 md:py-28">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            <StatBlock label="Cities" value="42" suffix="+" caption="Active across India" />
            <StatBlock label="Premium Sites" value="1850" suffix="+" caption="Across all formats" />
            <StatBlock label="Monthly Impressions" value="2.6" suffix="B" caption="Across the network" />
            <StatBlock label="Avg. Brand Lift" value="34" suffix="%" caption="Across campaigns" />
          </div>
        </div>
      </section>

      {/* SERVICES OVERVIEW */}
      <section className="relative bg-zinc-950 border-t border-white/5">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 md:py-32">
          <SectionHeading
            overline="What we do"
            title="Six formats. One unmissable network."
            subtitle="From trophy billboards to programmatic DOOH, cinema, in-film integrations, and prime-time radio, every format engineered for visibility, scale, and measurable brand impact."
          />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-5">
            {SERVICES.map((s, i) => {
              const Icon = s.icon;
              const span =
                i === 0 ? "md:col-span-7"
                : i === 1 ? "md:col-span-5"
                : i === 2 ? "md:col-span-4"
                : i === 3 ? "md:col-span-4"
                : i === 4 ? "md:col-span-4"
                : "md:col-span-12";
              return (
                <motion.div
                  key={s.id}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.05 }}
                  className={`group relative overflow-hidden rounded-2xl border border-white/5 bg-zinc-900 p-6 md:p-8 min-h-[260px] hover:border-blue-500/30 transition-colors duration-500 ${span}`}
                  data-testid={`service-card-${s.id}`}
                >
                  <img
                    src={s.img}
                    alt={s.title}
                    className="absolute inset-0 h-full w-full object-cover opacity-25 group-hover:opacity-45 group-hover:scale-105 transition-all duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 via-zinc-900/85 to-transparent" />
                  <div className="relative h-full flex flex-col justify-between">
                    <div className="flex items-center gap-3">
                      <Icon className="h-7 w-7 text-blue-400" />
                      <span className="font-mono-accent text-[10px] text-zinc-500">
                        0{i + 1} / 06
                      </span>
                    </div>
                    <div>
                      <h3 className="font-display text-2xl md:text-3xl font-bold tracking-tight">
                        {s.title}
                      </h3>
                      <p className="mt-2 text-zinc-400 text-sm md:text-base max-w-md">
                        {s.desc}
                      </p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
          <div className="mt-12">
            <Link
              to="/services"
              data-testid="services-explore-link"
              className="inline-flex items-center gap-2 text-sm font-semibold text-white hover:text-amber-300 transition-colors"
            >
              Explore all services <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* CLIENTS MARQUEE */}
      <section className="relative bg-zinc-950 border-t border-white/5">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16">
          <div className="mb-8 flex items-center justify-between">
            <div className="font-mono-accent text-[11px] text-zinc-500">
              Trusted by brands that refuse to be ignored
            </div>
            <Link
              to="/clients"
              className="hidden sm:inline-flex items-center gap-1.5 text-xs font-semibold text-zinc-400 hover:text-white"
              data-testid="clients-link"
            >
              All clients <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          <ClientMarquee />
        </div>
      </section>

      {/* TESTIMONIAL */}
      <section className="relative bg-zinc-950 border-t border-white/5">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 md:py-32">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="relative rounded-2xl border border-white/5 bg-zinc-900/50 p-8 md:p-10"
              >
                <Quote className="h-7 w-7 text-amber-400/70" />
                <p className="mt-6 text-xl md:text-2xl font-display font-medium leading-snug text-zinc-100">
                  "{t.quote}"
                </p>
                <div className="mt-6 flex items-center gap-3 text-sm">
                  <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center font-display font-bold">
                    {t.author[0]}
                  </div>
                  <div>
                    <div className="font-semibold text-white">{t.author}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="relative bg-zinc-950 border-t border-white/5">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 md:py-32">
          <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-blue-950 via-zinc-950 to-purple-950 p-10 md:p-16 grain">
            <div className="absolute -top-20 -right-20 h-64 w-64 rounded-full bg-amber-400/15 blur-3xl" />
            <div className="absolute -bottom-20 -left-20 h-64 w-64 rounded-full bg-blue-500/20 blur-3xl" />
            <div className="relative flex flex-col md:flex-row items-start md:items-end justify-between gap-8">
              <div className="max-w-2xl">
                <div className="font-mono-accent text-[11px] text-amber-400 mb-4">
                  ready when you are
                </div>
                <h3 className="font-display text-4xl md:text-6xl font-black leading-[1.05] tracking-tighter">
                  Let's plan a campaign that <span className="gradient-text italic">cannot be ignored.</span>
                </h3>
              </div>
              <Link
                to="/contact"
                data-testid="home-bottom-cta"
                className="inline-flex items-center gap-2 rounded-full bg-white text-zinc-950 px-7 py-4 text-sm font-bold hover:bg-amber-300 transition-all whitespace-nowrap"
              >
                Get a Quote <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
